import type { V2CreateAdminAgencyInput } from '@idc-forms/schemas/api-v2/admin-agencies'
import { CircleOff, CirclePlay, Info, Pencil, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteAgencyV2Mutation,
  useUpdateAgencyV2Mutation
} from '../hooks/use-agency-v2-mutations'
import type { AgencyV2AdminRow } from '../models/agency-admin-v2'
import { AgencyActiveBadge, AgencyDefaultBadge } from './agencies-admin-columns'
import { AgencyV2Form } from './agency-v2-form'

type AgencyV2DetailPanelProps = {
  agency: AgencyV2AdminRow
}

/**
 * Panel de detalle de la agencia contra la API v2. Espeja el panel v1 —mismo
 * `AgencyV2Form`, mismos badges, mismos acordeones de contacto y actividad— y
 * sólo cambia el transporte: direcciona por `agc_`, la empresa madre llega como
 * `{ id: cmp_, name }` y la autoría ya resuelta a `usr_`.
 *
 * Las acciones que el backend rechaza de plano se ocultan: Eliminar (bloqueada o
 * predeterminada) y Desactivar (predeterminada). Las guardas viven igual en el
 * backend: la UI sólo evita ofrecer una acción condenada a fallar.
 */
export function AgencyV2DetailPanel({ agency }: AgencyV2DetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateAgencyV2Mutation()
  const deleteMutation = useDeleteAgencyV2Mutation()
  const { has } = usePermissions()

  const isDeletable = !agency.blocked && !agency.default
  // La default no puede desactivarse (el backend responde 400).
  const canToggleActive = !(agency.active && agency.default)

  const toggleActive = () => {
    updateMutation.mutate({ id: agency.id, body: { active: !agency.active } })
  }

  if (mode === 'edit') {
    return (
      <AgencyV2Form
        defaultValues={{
          name: agency.name,
          code: agency.code,
          direction: agency.direction,
          email: agency.email,
          phone: agency.phone,
          // El SDK tipa el id de empresa como string plano; el form usa el
          // branded `cmp_` del schema. Mismo dato en runtime.
          company: agency.company?.id as V2CreateAdminAgencyInput['company'] | undefined,
          default: agency.default,
          active: agency.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={(values) => {
          updateMutation.mutate(
            { id: agency.id, body: values },
            {
              onSuccess: () => {
                setMode('view')
              }
            }
          )
        }}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">{agency.name}</h3>
            {agency.company && (
              <p className="truncate text-sm text-muted-foreground">{agency.company.name}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<AgencyActiveBadge active={agency.active} />} />
            <DetailFieldRow label="Código" value={agency.code} copyValue={agency.code} />
            <DetailFieldRow label="Empresa" value={agency.company?.name ?? '—'} />
            <DetailFieldRow
              label="Predeterminada"
              value={agency.default ? <AgencyDefaultBadge /> : 'No'}
            />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateAgencies)
              ? [
                  {
                    label: 'Editar agencia',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  ...(canToggleActive
                    ? [
                        {
                          label: agency.active ? 'Desactivar agencia' : 'Activar agencia',
                          icon: agency.active ? (
                            <CircleOff className="h-4 w-4" />
                          ) : (
                            <CirclePlay className="h-4 w-4" />
                          ),
                          variant: 'outline' as const,
                          disabled: updateMutation.isPending,
                          onClick: toggleActive
                        }
                      ]
                    : [])
                ]
              : []),
            ...(has(Permission.DeleteAgencies) && isDeletable
              ? [
                  {
                    label: 'Eliminar agencia',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        <TabsList className="w-full justify-normal rounded-none">
          <TabsTrigger className="flex gap-1" value="more-info">
            <Info className="size-4" /> Más información
          </TabsTrigger>
        </TabsList>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['contact', 'activity']}>
            <AccordionItem value="contact">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Contacto
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Correo"
                    value={agency.email || '—'}
                    copyValue={agency.email || undefined}
                  />
                  <DetailFieldRow label="Teléfono" value={agency.phone || '—'} />
                  <DetailFieldRow label="Dirección" value={agency.direction || '—'} />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(agency.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={agency.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(agency.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={agency.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar agencia</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{agency.name}”. Los clientes y negociaciones históricas que la
              referencian se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(agency.id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
